import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'

const DAY_MS = 24 * 60 * 60 * 1000

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
  { id: 'fresh', label: 'Fresh' },
  { id: 'unknown', label: 'Unknown' }
]

const SORT_OPTIONS = [
  { id: 'urgency_oldest', label: 'Urgency (Oldest First)' },
  { id: 'listed_oldest', label: 'First Listing (Oldest)' },
  { id: 'price_oldest', label: 'Price Update (Oldest)' }
]

const URGENCY_STYLES = {
  critical: 'bg-destructive/15 text-destructive border-destructive/30',
  high: 'bg-warning/15 text-warning border-warning/30',
  medium: 'bg-primary/15 text-primary border-primary/30',
  low: 'bg-success/15 text-success border-success/30',
  fresh: 'bg-muted/30 text-muted-foreground border-border/60',
  unknown: 'bg-muted/20 text-muted-foreground border-border/60'
}

function toMillis(value) {
  if (!value) return null
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.toDate === 'function') return value.toDate().getTime()
  if (value instanceof Date) return value.getTime()
  const parsed = Date.parse(String(value))
  return Number.isNaN(parsed) ? null : parsed
}

function getUrgencyMeta(product, nowMs) {
  const priceMs = toMillis(product?.priceUpdatedAt)
  const listedMs = toMillis(product?.createdAt)
  const referenceMs = priceMs ?? listedMs

  if (!referenceMs) {
    return {
      id: 'unknown',
      label: 'Unknown',
      rank: 0,
      days: null,
      detail: 'No price/listing date',
      referenceMs,
      priceMs,
      listedMs
    }
  }

  const days = Math.max(0, Math.floor((nowMs - referenceMs) / DAY_MS))
  const sourceLabel = priceMs ? 'price update' : 'listing'

  if (days >= 365) {
    return { id: 'critical', label: 'Critical', rank: 1, days, detail: `${days}d since ${sourceLabel}`, referenceMs, priceMs, listedMs }
  }
  if (days >= 180) {
    return { id: 'high', label: 'High', rank: 2, days, detail: `${days}d since ${sourceLabel}`, referenceMs, priceMs, listedMs }
  }
  if (days >= 90) {
    return { id: 'medium', label: 'Medium', rank: 3, days, detail: `${days}d since ${sourceLabel}`, referenceMs, priceMs, listedMs }
  }
  if (days >= 30) {
    return { id: 'low', label: 'Low', rank: 4, days, detail: `${days}d since ${sourceLabel}`, referenceMs, priceMs, listedMs }
  }
  return { id: 'fresh', label: 'Fresh', rank: 5, days, detail: `${days}d since ${sourceLabel}`, referenceMs, priceMs, listedMs }
}

const ProductSearchList = ({
  products,
  loading,
  error,
  lastSync,
  onSearch,
  onLoadMore,
  hasMore,
  onSyncMore,
  syncing,
  selectedId,
  onSelect
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const [urgencyFilter, setUrgencyFilter] = useState('all')
  const [sortMode, setSortMode] = useState('urgency_oldest')

  useEffect(() => {
    let active = true
    const term = searchTerm.trim()
    if (!term || !onSearch) {
      setSearchResults(null)
      setSearching(false)
      return () => {}
    }

    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const results = await onSearch(term)
        if (active) {
          setSearchResults(Array.isArray(results) ? results : [])
        }
      } catch (err) {
        console.error('Search failed:', err)
        if (active) setSearchResults([])
      } finally {
        if (active) setSearching(false)
      }
    }, 250)

    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [searchTerm, onSearch])

  const displayProducts = Array.isArray(searchResults) ? searchResults : (Array.isArray(products) ? products : [])
  const formatPrice = (value) => {
    if (!value && value !== 0) return '$0.00'
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  const { visibleProducts, urgencyCounts } = useMemo(() => {
    const nowMs = Date.now()
    const enriched = displayProducts.map((product) => ({
      product,
      urgency: getUrgencyMeta(product, nowMs)
    }))

    const counts = enriched.reduce((acc, row) => {
      const key = row.urgency.id
      acc[key] = (acc[key] || 0) + 1
      acc.all += 1
      return acc
    }, { all: 0, critical: 0, high: 0, medium: 0, low: 0, fresh: 0, unknown: 0 })

    let filtered = enriched
    if (urgencyFilter !== 'all') {
      filtered = filtered.filter((row) => row.urgency.id === urgencyFilter)
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortMode === 'listed_oldest') {
        const aListed = a.urgency.listedMs ?? Number.POSITIVE_INFINITY
        const bListed = b.urgency.listedMs ?? Number.POSITIVE_INFINITY
        return aListed - bListed
      }

      if (sortMode === 'price_oldest') {
        const aPrice = a.urgency.priceMs ?? a.urgency.listedMs ?? Number.NEGATIVE_INFINITY
        const bPrice = b.urgency.priceMs ?? b.urgency.listedMs ?? Number.NEGATIVE_INFINITY
        return aPrice - bPrice
      }

      const rankDiff = a.urgency.rank - b.urgency.rank
      if (rankDiff !== 0) return rankDiff
      const aRef = a.urgency.referenceMs ?? Number.NEGATIVE_INFINITY
      const bRef = b.urgency.referenceMs ?? Number.NEGATIVE_INFINITY
      return aRef - bRef
    })

    return { visibleProducts: sorted, urgencyCounts: counts }
  }, [displayProducts, urgencyFilter, sortMode])

  const hasSearchTerm = searchTerm.trim().length > 0

  return (
    <Card variant="solid" className="border border-border/60 shadow-elevation-1 flex flex-col max-h-[calc(100vh-220px)]">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Icon name="Search" size={18} className="text-primary" />
        <h3 className="font-semibold text-foreground">Products</h3>
        {hasSearchTerm && !searching && (
          <span className="text-xs text-muted-foreground">
            {visibleProducts.length} result(s)
          </span>
        )}
      </div>

      {/* Search Input */}
      <div className="relative mb-3">
        <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search name, SKU, or EAN-8..."
          className="w-full pl-9 pr-8 py-2 bg-input text-foreground placeholder:text-muted-foreground text-sm border border-border/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={14} />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTER_OPTIONS.map((opt) => {
            const active = urgencyFilter === opt.id
            const count = urgencyCounts?.[opt.id] || 0
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setUrgencyFilter(opt.id)}
                className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${
                  active
                    ? 'bg-primary/10 text-primary border-primary/30'
                    : 'bg-transparent text-muted-foreground border-border/60 hover:bg-muted/30 hover:text-foreground'
                }`}
              >
                {opt.label} ({count})
              </button>
            )
          })}
        </div>

        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          className="px-2.5 py-1.5 bg-input text-foreground border border-border/60 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary/20"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-3 p-2 bg-destructive/15 border border-destructive/30 rounded-lg">
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Scrollable List */}
      <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
        {searching && (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!searching && visibleProducts.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {hasSearchTerm ? (
              <>
                <Icon name="SearchX" size={28} className="mx-auto mb-2 text-muted-foreground/60" />
                <p className="text-sm">No results found</p>
              </>
            ) : displayProducts.length > 0 && urgencyFilter !== 'all' ? (
              <>
                <Icon name="Filter" size={28} className="mx-auto mb-2 text-muted-foreground/60" />
                <p className="text-sm">No products in this urgency bucket</p>
              </>
            ) : loading ? (
              <>
                <Icon name="Loader2" size={28} className="mx-auto mb-2 animate-spin text-muted-foreground/60" />
                <p className="text-sm">Loading products...</p>
              </>
            ) : (
              <>
                <Icon name="Package" size={28} className="mx-auto mb-2 text-muted-foreground/60" />
                <p className="text-sm">No products synced yet</p>
              </>
            )}
          </div>
        )}

        {!searching && visibleProducts.map(({ product, urgency }) => {
          const isSelected = selectedId === product.id
          const variants = product.variants || []
          const totalQty = variants.reduce((sum, v) => sum + (parseInt(v.inventoryQuantity, 10) || 0), 0)
          const price = product.price || variants[0]?.price || '0'

          return (
            <button
              key={product.id}
              onClick={() => onSelect(product)}
              className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg transition-colors border ${
                isSelected
                  ? 'bg-muted/40 border-primary/30'
                  : 'bg-transparent border-transparent hover:bg-muted/40 hover:border-border/60'
              }`}
            >
              {/* Thumbnail */}
              <div className="w-10 h-10 bg-background/80 rounded-lg overflow-hidden flex-shrink-0 border border-border/60">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={16} className="text-muted-foreground/60" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {product.title || 'Untitled'}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {(product.sku || variants[0]?.sku) && (
                    <span className="font-mono">{product.sku || variants[0]?.sku}</span>
                  )}
                  <span>{product.productType || 'Jewelry'}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${URGENCY_STYLES[urgency.id] || URGENCY_STYLES.unknown}`}>
                    {urgency.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{urgency.detail}</span>
                </div>
              </div>

              {/* Price & Stock */}
              <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                <span className="text-sm font-semibold text-foreground">{formatPrice(price)}</span>
                <span className={`text-xs ${totalQty <= 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {totalQty} in stock
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Footer: Load More + Sync */}
      <div className="pt-3 mt-3 border-t border-border/60 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {onLoadMore && hasMore && !hasSearchTerm && (
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              disabled={loading}
            >
              Load more
            </Button>
          )}
          {onSyncMore && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSyncMore}
              disabled={!!syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Store'}
            </Button>
          )}
        </div>
        {lastSync?.at && (
          <span className="text-xs text-muted-foreground">
            Synced: {new Date(lastSync.at).toLocaleString()}
          </span>
        )}
      </div>
    </Card>
  )
}

export default ProductSearchList
